import { useEffect, useRef } from 'react';
import { Spin, Typography } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { CreatePrestigeConfigurationResponse } from '@core';
import {
  useAnonymousAuth,
  useCreatePrestigeConfiguration,
} from '@hooks';

const { Text } = Typography;

export const ConfigBootstrapPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasRequestedConfiguration = useRef(false);
  const { isLoading: isAuthLoading, error: authError } = useAnonymousAuth();
  const createConfigurationMutation = useCreatePrestigeConfiguration();

  useEffect(() => {
    if (isAuthLoading || authError || hasRequestedConfiguration.current) {
      return;
    }

    hasRequestedConfiguration.current = true;

    // Users arriving from the marketing site have no config ID but may carry
    // `style` / `color` query params picked there. Forward them so the newly
    // created configuration opens pre-set to their choice.
    const style = searchParams.get('style') ?? undefined;
    const color = searchParams.get('color') ?? undefined;

    createConfigurationMutation.mutate(
      { style, color },
      {
        onSuccess: ({ buildNumber }: CreatePrestigeConfigurationResponse) => {
          navigate(`/${buildNumber}`, { replace: true });
        },
        onError: () => {
          hasRequestedConfiguration.current = false;
        },
      },
    );
  }, [
    authError,
    createConfigurationMutation,
    isAuthLoading,
    navigate,
    searchParams,
  ]);

  if (authError) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Authentication failed: {authError}</Text>
      </div>
    );
  }

  if (createConfigurationMutation.error) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">
          Failed to create configuration. Please refresh and try again.
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
      }}
    >
      <Spin size="large" />
      <Text>10+ Trillion Options Are Loading…</Text>
    </div>
  );
};
